import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/theme-light.css";
import { applyTheme, getTheme } from "@/utils/theme";
import { startAutoUpdate } from "@/utils/autoUpdate";
import App from "@/App";
import { Capacitor } from '@capacitor/core';

applyTheme(getTheme());
startAutoUpdate();

console.log('[RVINCENTE] Main bundle loaded successfully at', new Date().toISOString());

// Register native push notification click listener BEFORE React mounts.
// This prevents the cold-start race condition where the notification tap event
// fires before React's useEffect can set up the listener.
if (Capacitor.isNativePlatform()) {
  import('@capacitor/push-notifications').then(({ PushNotifications }) => {
    PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
      const data = action.notification?.data || {};
      const link = data.link || data.url;
      if (!link) return;
      // IMPORTANT: only `app.rvincente.it` is INTERNAL to the PWA. Other
      // rvincente.it subdomains (www.rvincente.it / blog.rvincente.it / ...)
      // are external public websites and MUST open in the system browser,
      // not via React Router (no such route exists in the PWA).
      const isInternal = link.startsWith('/') || /^https?:\/\/app\.rvincente\.it/.test(link);
      if (!isInternal) {
        // External URL — open exactly like a bookmaker card link does
        // (BookmakerCard.jsx uses <a target="_blank" rel="noopener">). This
        // approach works on every device because Capacitor's WebViewClient
        // intercepts the resulting navigation, sees the URL is outside
        // `app.rvincente.it`, and delegates to the OS → system browser opens.
        try {
          const a = document.createElement('a');
          a.href = link;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch {
          // Final fallback (very rare)
          window.location.href = link;
        }
        return;
      }
      const path = link.replace(/^https?:\/\/app\.rvincente\.it/, '') || '/';
      localStorage.setItem('pending_notification_nav', path);
      window.dispatchEvent(new Event('notificationNav'));
    });

    // Quando arriva una nuova notifica mentre l'app e' in foreground,
    // invalida subito le query pertinenti cosi' l'utente vede i nuovi
    // dati senza dover chiudere/riaprire l'app.
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const data = notification?.data || {};
      const type = data.type || '';
      try {
        if (window.queryClient) {
          if (type === 'vip_prediction' || type === 'vip_predictions') {
            window.queryClient.invalidateQueries({ queryKey: ['vip-predictions'] });
          }
          if (type === 'chat_message' || type === 'new_message') {
            window.queryClient.invalidateQueries({ queryKey: ['conversations'] });
          }
          if (type === 'victory' || type === 'new_victory') {
            window.queryClient.invalidateQueries({ queryKey: ['victory-gallery'] });
          }
          // Always invalidate conversations — admin might be watching
          window.queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }
      } catch {}
    });
  }).catch(() => {});
}

// apri.rvincente.it = bridge page that hands email links to the installed app
const isBridgeHost = window.location.hostname.startsWith('apri.')
  || new URLSearchParams(window.location.search).has('__bridge');
const OpenInAppPage = React.lazy(() => import('./pages/OpenInAppPage'));

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {isBridgeHost ? (
      <React.Suspense fallback={null}><OpenInAppPage /></React.Suspense>
    ) : (
      <App />
    )}
  </React.StrictMode>,
);

// Hide the boot splash once React has committed its first render.
// Uses two rAFs to guarantee the browser has painted before we fade out.
if (typeof window !== 'undefined') {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try { window.__hideBootSplash && window.__hideBootSplash(); } catch (_) { /* no-op */ }
    });
  });
}
